import _ from 'underscore';

import {assert} from 'Asserts';
import ModelTypeConstants, {SchemaType} from 'constants/ModelTypeConstants';
import {TagSchemaSummary} from 'constants/RegistryConstants';
import {BatchSchema} from 'constants/SampleConstants';
import {FromServerDataTableColumn, FromServerDataTableConfig} from 'notebook/data-tables/DataTableConstants';
import {LOOKUP_FIELD_FIELD_TYPE_EXP_COND} from 'notebook/tables/lookup-field/LookupFieldDefinitionParser';
import {FieldType, RequiredLinkTagSchema, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {TagSchemaSummaryWithFieldsGraphQL} from 'schemas/useTagSchemaSummariesWithFields';

import {
  FilterSpecifier,
  InputSampleLookupSpecifier,
  LookupSpecifier,
  SourceSpecifier,
  StageRunSpecifierFieldType,
} from './LookupFieldDefinitionConstants';

// This constant limits how deep the field tree can go.
//
// - Please don't increase this limit by much. We generate the field tree without any pruning, so if you increase the limit,
//   you could worsen performance exponentially!
//
// - On the other hand, this limit should be perfectly safe to reduce. The limit is only enforced at configuration time, not runtime.
//   Even if the limit is reduced too far, existing LOOKUPFIELDs will not break.
const MAX_CONFIGURABLE_FIELD_DEPTH = 5;

export enum DATA_TYPE {
  DATA_TABLE = 'DATA_TABLE',
  SCHEMA_FIELD = 'SCHEMA_FIELD',
  SAMPLE_ID = 'SAMPLE_ID',
  INPUT_SAMPLE = 'INPUT_SAMPLE',
}

export interface FieldsChainField {
  schemaType: SchemaType;
  uniqueFieldId: string;
  parentFieldId: string | null;
  name: string;
  tagSchema: TagSchemaSummaryWithFieldsGraphQL | null;
  fieldType: FieldType;
  requiredLink: {
    tagSchema: TagSchemaSummaryWithFieldsGraphQL | TagSchemaSummary | null;
  };
}

function getSchemaIdForFieldsChainField(fieldsChainField: FieldsChainField): string {
  switch (fieldsChainField.schemaType) {
    case ModelTypeConstants.TAG_SCHEMA:
      return fieldsChainField.tagSchema!.id;
    default:
      throw new Error(`Unexpected schema type ${fieldsChainField.schemaType}`);
  }
}

export function generateInputSampleLookupSpecifier({
  fieldType,
  schemaId,
  stageRunId,
  stageRunSampleId,
}: {
  fieldType: StageRunSpecifierFieldType;
  schemaId: string | null;
  stageRunId: string | null;
  stageRunSampleId: string | null;
}): InputSampleLookupSpecifier {
  return {
    fieldName: null,
    fieldType,
    schemaId: schemaId || null,
    schemaName: null,
    stageRunId,
    stageRunSampleId,
    filterSpecifiers: [],
    sourceSpecifier: null,
  };
}

export function generateLookupSpecifier({
  topLevelSchema,
  fieldsChain,
  dataType,
  selectedSchemaField,
  selectedSchemaFieldSchemaType,
  selectedLinkTagSchema,
  selectedLinkBatchSchema,
  sampleSchemaFieldType,
  dataTableConfig,
  dataTableColumn,
  filterSpecifiers,
  sourceLookupSpecifier,
}: {
  topLevelSchema: TagSchemaSummaryWithFieldsGraphQL | null;
  fieldsChain: Array<FieldsChainField>;
  dataType: DATA_TYPE;
  selectedSchemaField: SchemaField | null;
  selectedSchemaFieldSchemaType: SchemaType | null;
  selectedLinkTagSchema: TagSchemaSummaryWithFieldsGraphQL | null;
  selectedLinkBatchSchema: BatchSchema | null;
  sampleSchemaFieldType: StageRunSpecifierFieldType | null;
  dataTableConfig: FromServerDataTableConfig | null;
  dataTableColumn: FromServerDataTableColumn | null;
  filterSpecifiers: Array<FilterSpecifier>;
  sourceLookupSpecifier: LookupSpecifier | null;
}): LookupSpecifier | null {
  if (topLevelSchema == null) {
    return null;
  }

  let sourceSpecifier = sourceLookupSpecifier
    ? ({lookupSpecifier: sourceLookupSpecifier} as SourceSpecifier)
    : null;
  // Iterate up to the second element (1) to exclude the innermost field.
  for (let i = fieldsChain.length - 1; i >= 1; i--) {
    sourceSpecifier = {
      lookupSpecifier: {
        schemaName: null,
        schemaId: getSchemaIdForFieldsChainField(fieldsChain[i]),
        fieldName: fieldsChain[i].name,
        fieldType: null,
        sourceSpecifier,
      },
    };
  }
  if (fieldsChain.length > 0 && (dataType === DATA_TYPE.DATA_TABLE || dataType === DATA_TYPE.SCHEMA_FIELD)) {
    // The final lookup for data table and schema fields is specified outside the fields chain
    // so include the innermost field as a source specifier.
    sourceSpecifier = {
      lookupSpecifier: {
        schemaName: null,
        schemaId: getSchemaIdForFieldsChainField(fieldsChain[0]),
        fieldName: fieldsChain[0].name,
        fieldType: null,
        sourceSpecifier,
      },
    };
  }

  let schemaId;
  let fieldName;
  let fieldType;
  switch (dataType) {
    case DATA_TYPE.DATA_TABLE:
      if (dataTableConfig == null || dataTableColumn == null) {
        return null;
      }
      schemaId = dataTableConfig.assaySchema.id;
      fieldName = dataTableColumn.name;
      fieldType = null;
      break;

    case DATA_TYPE.SCHEMA_FIELD:
      if (selectedSchemaField == null || selectedSchemaFieldSchemaType == null) {
        return null;
      }
      if (selectedSchemaFieldSchemaType === ModelTypeConstants.BATCH_SCHEMA) {
        assert(selectedLinkBatchSchema != null, 'Batch schema is expected when schema type is BATCH');
        schemaId = selectedLinkBatchSchema.id;
      } else {
        assert(selectedLinkTagSchema != null, 'Tag schema is expected when schema type is ENTITY');
        schemaId = selectedLinkTagSchema.id;
      }
      fieldName = selectedSchemaField.name;
      fieldType = null;
      break;

    case DATA_TYPE.SAMPLE_ID:
      if (fieldsChain.length > 0) {
        sourceSpecifier = {
          lookupSpecifier: {
            schemaName: null,
            schemaId: getSchemaIdForFieldsChainField(fieldsChain[0]),
            fieldName: fieldsChain[0].name,
            fieldType: null,
            sourceSpecifier,
          },
        };
      }
      assert(sampleSchemaFieldType != null, 'Expected a field type for sample ID lookup');
      schemaId = null;
      fieldName = null;
      fieldType = sampleSchemaFieldType;
      break;

    default:
      throw new Error(`Unhandled data type: ${dataType}`);
  }

  return {
    schemaName: null,
    schemaId,
    fieldName,
    fieldType,
    filterSpecifiers,
    sourceSpecifier,
  };
}

export function generateExpCondLookupSpecifier({
  experimentalConditionName,
  stageRunId,
  stageRunSampleId,
}: {
  experimentalConditionName: string;
  stageRunId: string | null;
  stageRunSampleId: string | null;
}): InputSampleLookupSpecifier {
  return {
    fieldName: experimentalConditionName,
    fieldType: LOOKUP_FIELD_FIELD_TYPE_EXP_COND,
    schemaId: null,
    schemaName: null,
    stageRunId,
    stageRunSampleId,
    filterSpecifiers: [],
    sourceSpecifier: null,
  };
}

export function generateUniqueFieldId({
  parentFieldId,
  id,
}: {
  parentFieldId: string | null;
  id: string | number;
}): string {
  return parentFieldId == null ? `${id}` : `${parentFieldId}-${id}`;
}

export function isFieldEntitySchemaLink(field: FieldsChainField | SchemaField): boolean {
  return (
    field.fieldType === FieldType.BIOENTITY_LINK &&
    field.requiredLink != null &&
    // eslint-disable-next-line benchlint/no-in-operator
    'tagSchema' in field.requiredLink &&
    field.requiredLink.tagSchema != null
  );
}

export function isFieldBatchSchemaLink(field: FieldsChainField | SchemaField): boolean {
  return (
    field.fieldType === FieldType.BATCH_LINK &&
    field.requiredLink != null &&
    // eslint-disable-next-line benchlint/no-in-operator
    'batchSchema' in field.requiredLink &&
    field.requiredLink.batchSchema != null
  );
}

function addFieldsChainFields({
  tagSchema,
  parentFieldId,
  depth,
  tagSchemasById,
}: {
  tagSchema: TagSchemaSummaryWithFieldsGraphQL;
  parentFieldId: string | null;
  depth: number;
  tagSchemasById: Record<string, TagSchemaSummaryWithFieldsGraphQL>;
}): Array<FieldsChainField> {
  const nestedFields: Array<FieldsChainField> = [];
  if (tagSchema.fieldsJson == null) {
    return nestedFields;
  }
  for (const field of tagSchema.fieldsJson) {
    const isEntitySchemaLink = isFieldEntitySchemaLink(field);
    const isBatchSchemaLink = isFieldBatchSchemaLink(field);

    if (!isEntitySchemaLink && !isBatchSchemaLink) {
      continue;
    }

    const uniqueFieldId = generateUniqueFieldId({parentFieldId, id: field.id});
    nestedFields.push({
      ...field,
      uniqueFieldId,
      parentFieldId,
      tagSchema,
      schemaType: ModelTypeConstants.TAG_SCHEMA,
      requiredLink: {
        tagSchema:
          // eslint-disable-next-line benchlint/no-in-operator
          field.requiredLink && 'tagSchema' in field.requiredLink ? field.requiredLink.tagSchema : null,
      },
    });

    let tagSchemaFinal;
    if (isEntitySchemaLink) {
      tagSchemaFinal = tagSchemasById[(field.requiredLink as RequiredLinkTagSchema).tagSchema!.id];
    }

    if (tagSchemaFinal == null) {
      // Most likely the schema is not loaded in tagSchemasById yet
      return nestedFields;
    }

    nestedFields.push(
      ...getNestedSchemaFields({
        tagSchema,
        parentFieldId: uniqueFieldId,
        depth: depth + 1,
        tagSchemasById,
      })
    );
  }

  return nestedFields;
}

function getNestedSchemaFields({
  tagSchema,
  parentFieldId,
  depth,
  tagSchemasById,
}: {
  tagSchema: TagSchemaSummaryWithFieldsGraphQL;
  parentFieldId: string | null;
  depth: number;
  tagSchemasById: Record<string, TagSchemaSummaryWithFieldsGraphQL>;
}): Array<FieldsChainField> {
  if (depth > MAX_CONFIGURABLE_FIELD_DEPTH) {
    return [];
  }
  return addFieldsChainFields({
    tagSchema,
    parentFieldId,
    depth,
    tagSchemasById,
  });
}

export function getNestedSchemaFieldsForTopLevelSchema({
  tagSchema,
  tagSchemasById,
}: {
  tagSchema: TagSchemaSummaryWithFieldsGraphQL;
  tagSchemasById: Record<string, TagSchemaSummaryWithFieldsGraphQL>;
}): Array<FieldsChainField> {
  return [
    ...getNestedSchemaFields({
      tagSchema,
      parentFieldId: null,
      depth: 0,
      tagSchemasById,
    }),
  ];
}

export function getFieldsChainFromLeafToRoot({
  allFields,
  uniqueFieldId,
}: {
  allFields: Array<FieldsChainField>;
  uniqueFieldId: string | null;
}): Array<FieldsChainField> {
  const selectedFieldsChain = [];
  let curFieldId = uniqueFieldId;
  while (curFieldId != null) {
    const field: FieldsChainField | undefined = _.findWhere(allFields, {uniqueFieldId: curFieldId});
    if (field == null) {
      assert(false, `Field with id ${curFieldId} wasn't found.`);
      break;
    }
    selectedFieldsChain.push(field);
    curFieldId = field.parentFieldId;
  }
  return selectedFieldsChain;
}
